/**
 * The month buckets an organization's generation activity is reported in.
 *
 * Deliberately free of Mongo, baseql and date-fns: this is the one place that decides what a "month" is,
 * and both sides of the wire have to agree with it — the aggregation groups on `%Y-%m`, the card labels
 * the bars — so it is plain arithmetic that can be unit-tested without a database or a clock.
 *
 * **Everything here is UTC.** `$dateToString` buckets in UTC unless told otherwise, so the window
 * boundaries are computed the same way; mixing the two would let a run near a month boundary be matched
 * into the range but bucketed outside it, and vanish from the counts. The visible consequence is that a
 * run finishing shortly before local midnight on the last of the month can be reported under the next
 * month for a user east of UTC.
 */

/** How many months the card may ask for at once, and what it gets when it asks for nothing sensible. */
export const MONTHS_WINDOW_DEFAULT = 12;
const MONTHS_WINDOW_MIN = 1;
const MONTHS_WINDOW_MAX = 36;

/** What the aggregation counts per bucket. `done` is never stored — see `fillMonths`. */
export interface MonthCounts {
  total: number;
  failed: number;
}

/** One bucket as the client reads it. */
export interface MonthBucket extends MonthCounts {
  /** `YYYY-MM`, exactly what `$dateToString: { format: '%Y-%m' }` produces. */
  month: string;
  done: number;
}

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Identity for one month bucket, unique across organizations.
 *
 * The client's entity store is keyed by class, so a bucket identified by its month alone would be shared
 * between every organization that has a `2026-07` — and merged with it. Stable across refetches, which is
 * the other half of what the store wants.
 */
export const generationMonthId = (organizationId: string, month: string) => `${organizationId}:${month}`;

/** Keeps a caller from asking for an unbounded window. */
export const clampMonths = (months: number | null | undefined): number => {
  if (!months || !Number.isFinite(months)) return MONTHS_WINDOW_DEFAULT;
  return Math.min(MONTHS_WINDOW_MAX, Math.max(MONTHS_WINDOW_MIN, Math.trunc(months)));
};

/** `YYYY-MM` for the UTC month a moment falls in. */
export const monthKey = (date: Date): string => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;

/** Splits a key back into its parts, for a client that wants to render a month name. */
export const parseMonthKey = (key: string): { year: number; month: number } => {
  const [year, month] = key.split('-');
  return { year: Number(year), month: Number(month) };
};

/**
 * First instant of the window — the start of the UTC month `months - 1` back, so that a window of 12
 * ends with the current month rather than excluding it.
 */
export const monthWindowStart = (now: Date, months: number): Date =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (months - 1), 1));

/** The window's month keys, oldest first. `Date.UTC` rolls the year back on its own for a negative month. */
export const monthWindow = (now: Date, months: number): string[] =>
  Array.from({ length: months }, (_, index) =>
    monthKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (months - 1) + index, 1))),
  );

/**
 * One bucket per month in the window, whether anything happened in it or not.
 *
 * A month with no runs has to come back as a zero rather than be left out: the graph reads the array
 * positionally, and dropping the quiet months would silently compress the axis so that a gap of half a
 * year looks like two consecutive months.
 *
 * `done` is derived here rather than counted separately, so "completed" and "failed" cannot add up to
 * something other than the total the bar is drawn from.
 */
export const fillMonths = (window: string[], counts: Iterable<MonthCounts & { month: string }>): MonthBucket[] => {
  const byMonth = new Map<string, MonthCounts>();
  for (const bucket of counts) byMonth.set(bucket.month, bucket);

  return window.map(month => {
    const counts = byMonth.get(month);
    const total = counts?.total ?? 0;
    const failed = counts?.failed ?? 0;
    return { month, total, failed, done: total - failed };
  });
};
